import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { CircularProgress, FormLabel, Grid } from "@material-ui/core";

import { getShipmentDetails } from "api/shipment";

import { StatusLabel } from "components/Custom";
import { ExternalLink } from "components/Custom/Links";
import DefaultTable from "components/Table";
import Widget from "components/Widget";

import { useLayoutState } from "context/LayoutContext";

import { getCarrierTrackingLink } from "pages/shipments/utils";
import { formatBilling } from "utils/carrier";
import { initialAPIListValue } from "utils/constants";
import { timezoneFormat } from "utils/date";

import useStyles from "pages/shipments/styles";

export default function ShipmentPreview({ shipment }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const { isMobile } = useLayoutState();
  const [details, setDetails] = useState(initialAPIListValue);

  const previewHeaders = [
    {
      id: "sku",
      type: "string",
      render: (item) => (
        <Grid container>
          <Grid item>
            <span style={{ whiteSpace: "nowrap" }}>
              {item.quantity > 1
                ? `${item.product.sku}  (${item.quantity})`
                : item.product.sku}
            </span>
          </Grid>
        </Grid>
      ),
    },
    {
      id: "tracking_number",
      render: (item) =>
        item.tracking_number ? (
          <ExternalLink
            to={getCarrierTrackingLink(shipment.ship_via, item.tracking_number)}
            children={item.tracking_number}
          />
        ) : (
          "-"
        ),
    },
    {
      id: "box_size",
      render: (item) =>
        item.product.boxes.length ? (
          <span
            style={{ whiteSpace: "nowrap" }}
          >{`${item.product.boxes[0].length}″ x ${item.product.boxes[0].width}″ x ${item.product.boxes[0].height}″`}</span>
        ) : (
          "Box not found!"
        ),
      style: { whiteSpace: "nowrap" },
    },
  ];

  useEffect(() => {
    if (details.isFetching) {
      getShipmentDetails({
        params: {
          shipment_id: shipment.id,
          limit: 9999,
        },
        responseSetter: (res) => {
          setDetails({ items: res.results, isFetching: false });
        },
      });
    }
  }, [shipment.id, details.isFetching, isMobile]);

  return (
    <Grid container spacing={2}>
      <Grid item lg={12} xs={12}>
        <Widget>
          <Grid container>
            <Grid item lg={3} xs={12}>
              <div>
                <FormLabel component="h6" className={classes.label}>
                  {t("shipment_number")}
                </FormLabel>

                <span>{shipment.shipment_number}</span>
              </div>
            </Grid>
            <Grid item lg={3} xs={12}>
              <FormLabel component="h6" className={classes.label}>
                {t("ship_via")}
              </FormLabel>
              <span>{shipment.ship_via || "-"}</span>
            </Grid>
            <Grid item lg={3} xs={12}>
              <FormLabel component="h6" className={classes.label}>
                {t("billed_to")}
              </FormLabel>
              <span>{formatBilling(shipment)}</span>
            </Grid>
            <Grid item lg={3} xs={12}>
              <div>
                <FormLabel component="h6" className={classes.label}>
                  {t("tracking_number")}
                </FormLabel>

                <span>
                  {shipment.tracking_number ? (
                    <ExternalLink
                      to={getCarrierTrackingLink(
                        shipment.ship_via,
                        shipment.tracking_number,
                      )}
                      children={shipment.tracking_number}
                    />
                  ) : (
                    "-"
                  )}
                </span>
              </div>
            </Grid>
            <Grid item lg={3} xs={12}>
              <div>
                <FormLabel component="h6" className={classes.label}>
                  {t("customer")}
                </FormLabel>
                <span>{shipment.order ? shipment.order.customer_id : "-"}</span>
              </div>
            </Grid>

            <Grid item lg={3} xs={12}>
              <FormLabel component="h6" className={classes.label}>
                {t("created_at")}
              </FormLabel>
              <span>{timezoneFormat(shipment.created_date)}</span>
            </Grid>

            <Grid item lg={3} xs={12}>
              <FormLabel component="h6" className={classes.label}>
                {t("delivery_address")}
              </FormLabel>
              <span>
                {shipment.address.line_1 + (shipment.address.line_2 || "")}
              </span>
              <br />
              <span>{shipment.address.city + ", "}</span>
              <span>{shipment.address.state + ", "}</span>
              <span>{shipment.address.country + ", "}</span>
              <span>{shipment.address.zip_code}</span>
            </Grid>
            <Grid item lg={3} xs={12}>
              <FormLabel component="h6" className={classes.label}>
                {t("contact_name")}
              </FormLabel>
              <span>{shipment.address.customer}</span>
            </Grid>
            <Grid item lg={3} xs={12}>
              <FormLabel component="h6" className={classes.label}>
                {t("status")}
              </FormLabel>
              <StatusLabel status={shipment.status} />
            </Grid>
          </Grid>
        </Widget>
      </Grid>
      <Grid item lg={12} xs={12}>
        <Widget title={t("items")}>
          {details.isFetching ? (
            <CircularProgress />
          ) : !details.items.length ? (
            t("no_results_found")
          ) : (
            <>
              <Grid container spacing={0}>
                <DefaultTable data={details} headers={previewHeaders} />
              </Grid>
            </>
          )}
        </Widget>
      </Grid>
    </Grid>
  );
}
